import { useEffect, useState } from "react";
import { useLang } from "../i18n";
import { Logo } from "./common";
import { PHONE_DISPLAY, wa, DEFAULT_MSG_PT, DEFAULT_MSG_EN, scrollToId } from "../lib/site";

const LINKS = [
    { id: "inicio", key: "home" },
    { id: "servicos", key: "services" },
    { id: "ti", key: "ti" },
    { id: "spda", key: "spda" },
    { id: "obras", key: "works" },
    { id: "cobertura", key: "coverage" },
    { id: "depoimentos", key: "testimonials" },
    { id: "orcamento", key: "quote" },
];

export default function Navbar() {
    const { lang, setLang, t } = useLang();
    const [scrolled, setScrolled] = useState(false);
    const [open, setOpen] = useState(false);

    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 24);
        onScroll();
        window.addEventListener("scroll", onScroll, { passive: true });
        return () => window.removeEventListener("scroll", onScroll);
    }, []);

    const go = (id) => {
        setOpen(false);
        setTimeout(() => scrollToId(id), open ? 180 : 0);
    };

    return (
        <header
            className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
                scrolled
                    ? "bg-obsidian/85 backdrop-blur-xl border-b border-volt/10"
                    : "bg-transparent border-b border-transparent"
            }`}
        >
            <div className="max-w-7xl mx-auto px-5 md:px-10 py-3.5 flex items-center justify-between gap-4">
                <button
                    data-testid="nav-logo"
                    onClick={() => go("inicio")}
                    className="flex items-center gap-3 group"
                >
                    <span className="relative">
                        <Logo size={44} />
                        <span className="absolute -top-0.5 -right-0.5 w-2 h-2 rounded-full bg-emerald-400 pulse-dot" />
                    </span>
                    <span className="hidden xl:block text-left leading-none">
                        <span className="block font-display font-extrabold tracking-wide text-lg">
                            ANG
                        </span>
                        <span className="block font-mono text-[9px] uppercase tracking-[0.22em] text-slate-400 mt-1 whitespace-nowrap">
                            Engenharia · SP
                        </span>
                    </span>
                </button>

                <nav className="hidden lg:flex items-center gap-7">
                    {LINKS.map((l) => (
                        <button
                            key={l.id}
                            data-testid={`nav-link-${l.key}`}
                            onClick={() => go(l.id)}
                            className="font-mono text-[11px] uppercase tracking-[0.18em] text-slate-300 hover:text-volt transition-colors whitespace-nowrap"
                        >
                            {t.nav[l.key]}
                        </button>
                    ))}
                </nav>

                <div className="flex items-center gap-3">
                    <div className="hidden sm:flex items-center rounded-full border border-white/15 p-0.5 font-mono text-[10px]">
                        <button
                            data-testid="language-toggle-pt"
                            onClick={() => setLang("pt")}
                            className={`px-2.5 py-1 rounded-full transition-all ${
                                lang === "pt"
                                    ? "bg-volt text-obsidian font-bold"
                                    : "text-slate-400 hover:text-white"
                            }`}
                        >
                            PT
                        </button>
                        <button
                            data-testid="language-toggle-en"
                            onClick={() => setLang("en")}
                            className={`px-2.5 py-1 rounded-full transition-all ${
                                lang === "en"
                                    ? "bg-volt text-obsidian font-bold"
                                    : "text-slate-400 hover:text-white"
                            }`}
                        >
                            EN
                        </button>
                    </div>

                    <a
                        data-testid="whatsapp-header-cta"
                        href={wa(lang === "pt" ? DEFAULT_MSG_PT : DEFAULT_MSG_EN)}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="hidden md:inline-flex items-center gap-2 bg-volt text-obsidian font-bold text-sm px-5 py-2.5 rounded-full hover:bg-volt/90 hover:shadow-[0_0_28px_rgba(255,230,0,0.4)] transition-all whitespace-nowrap"
                    >
                        {t.nav.cta}
                    </a>

                    <button
                        data-testid="nav-mobile-toggle"
                        onClick={() => setOpen(!open)}
                        className="lg:hidden w-10 h-10 rounded-full border border-white/15 flex flex-col items-center justify-center gap-1.5"
                        aria-label="Menu"
                    >
                        <span className={`w-4 h-px bg-white transition-transform ${open ? "rotate-45 translate-y-[3.5px]" : ""}`} />
                        <span className={`w-4 h-px bg-white transition-transform ${open ? "-rotate-45 -translate-y-[3.5px]" : ""}`} />
                    </button>
                </div>
            </div>

            {open && (
                <div className="lg:hidden bg-obsidian/95 backdrop-blur-xl border-t border-white/10 px-6 py-5 flex flex-col gap-4">
                    {LINKS.map((l) => (
                        <button
                            key={l.id}
                            data-testid={`nav-link-mobile-${l.key}`}
                            onClick={() => go(l.id)}
                            className="text-left font-display font-bold text-lg text-slate-200 hover:text-volt transition-colors"
                        >
                            {t.nav[l.key]}
                        </button>
                    ))}
                    <a
                        data-testid="whatsapp-mobile-cta"
                        href={wa(lang === "pt" ? DEFAULT_MSG_PT : DEFAULT_MSG_EN)}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="mt-2 inline-flex items-center justify-center gap-2 bg-volt text-obsidian font-bold px-5 py-3 rounded-full"
                    >
                        {t.nav.cta} · {PHONE_DISPLAY}
                    </a>
                </div>
            )}
        </header>
    );
}
